import { FormEvent, useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@/components/ui/Alert';
import styles from './OpenFundEarlyLeadCapture.module.scss';

/** Success toast lifetime — long enough to read, short enough not to block the wizard. */
export const EARLY_LEAD_SUCCESS_DISMISS_MS = 3500;

export const OPEN_FUND_EARLY_LEAD_PANEL_ID = 'open-fund-early-lead-panel';

interface OpenFundEarlyLeadCaptureProps {
  onSave: (contact: { email: string; phone: string }) => Promise<boolean>;
  isSaving: boolean;
  /** Contact was saved (or restored from a draft); hides the capture form after the flash. */
  contactSaved: boolean;
  /** Brief success banner after a fresh save — not shown when only resuming a draft. */
  showSuccessFlash: boolean;
  onSuccessFlashDismiss: () => void;
  /** When true, the whole block stays hidden (resume / already saved). */
  startCollapsed: boolean;
  panelId?: string;
  emailInputRef?: RefObject<HTMLInputElement | null>;
}

const OpenFundEarlyLeadCapture = ({
  onSave,
  isSaving,
  contactSaved,
  showSuccessFlash,
  onSuccessFlashDismiss,
  startCollapsed,
  panelId = OPEN_FUND_EARLY_LEAD_PANEL_ID,
  emailInputRef,
}: OpenFundEarlyLeadCaptureProps) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(startCollapsed);
  const saveInFlightRef = useRef(false);
  const [saveLocked, setSaveLocked] = useState(false);
  const localEmailRef = useRef<HTMLInputElement>(null);
  const emailRef = emailInputRef ?? localEmailRef;

  const dismissSuccess = useCallback(() => {
    onSuccessFlashDismiss();
    if (contactSaved) {
      setCollapsed(true);
    }
  }, [contactSaved, onSuccessFlashDismiss]);

  useEffect(() => {
    if (startCollapsed) {
      setCollapsed(true);
    }
  }, [startCollapsed]);

  useEffect(() => {
    if (!showSuccessFlash) return;
    const timer = window.setTimeout(dismissSuccess, EARLY_LEAD_SUCCESS_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [showSuccessFlash, dismissSuccess]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (isSaving || saveInFlightRef.current || saveLocked) return;

    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    if (!trimmedEmail && !trimmedPhone) {
      setError(t('openFundRequest.earlyLead.errors.contactRequired'));
      return;
    }
    setError(null);
    saveInFlightRef.current = true;
    setSaveLocked(true);
    try {
      const saved = await onSave({ email: trimmedEmail, phone: trimmedPhone });
      if (saved) {
        setEmail('');
        setPhone('');
      } else {
        setError(t('openFundRequest.earlyLead.errors.saveFailed'));
      }
    } finally {
      saveInFlightRef.current = false;
      setSaveLocked(false);
    }
  };

  const saveDisabled = isSaving || contactSaved || saveLocked || saveInFlightRef.current;

  if (collapsed) {
    return null;
  }

  if (showSuccessFlash) {
    return (
      <section className={styles.wrapper} aria-labelledby="open-fund-early-lead-title">
        <h3 id="open-fund-early-lead-title" className="sr-only">
          {t('openFundRequest.earlyLead.title')}
        </h3>
        <Alert type="success" inline className={styles.successAlert} message={t('openFundRequest.earlyLead.saved')} onClose={dismissSuccess} />
      </section>
    );
  }

  return (
    <section id={panelId} className={styles.wrapper} aria-labelledby="open-fund-early-lead-title">
      <h3 id="open-fund-early-lead-title" className={`body-sm-semibold ${styles.title}`}>
        {t('openFundRequest.earlyLead.title')}
      </h3>
      <p className={`body-xs-sm-regular ${styles.hint}`}>{t('openFundRequest.earlyLead.hint')}</p>
      <form className={styles.form} onSubmit={handleSubmit}>
        <label className={styles.field}>
          <span className={`body-xs-sm-regular ${styles.fieldLabel}`}>{t('openFundRequest.fields.email')}</span>
          <input
            ref={emailRef}
            type="email"
            autoComplete="email"
            className={styles.input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={saveDisabled}
          />
        </label>
        <span className={styles.or}>{t('openFundRequest.earlyLead.or')}</span>
        <label className={styles.field}>
          <span className={`body-xs-sm-regular ${styles.fieldLabel}`}>{t('openFundRequest.fields.phone')}</span>
          <input type="tel" autoComplete="tel" className={styles.input} value={phone} onChange={(e) => setPhone(e.target.value)} disabled={saveDisabled} />
        </label>
        {error && (
          <p role="alert" className={styles.error}>
            {error}
          </p>
        )}
        <button type="submit" className={styles.saveButton} disabled={saveDisabled}>
          {isSaving || saveLocked ? t('submitting') : t('openFundRequest.earlyLead.save')}
        </button>
      </form>
    </section>
  );
};

export default OpenFundEarlyLeadCapture;
